/**
 * Authority Management Types
 * 권한 설정 관련 타입 정의
 */

// ======== 권한 정보 ========

/** 권한 정보 */
export interface Authority {
    /** 권한 코드 (PK) */
    prmCode: string;
    /** 권한명 */
    prmName: string;
    /** 권한설명 */
    prmDesc: string;
    /** 기반 사용자 유형 (C: 본사, S: 협력사, R: 라이더) */
    baseUserType: string;
    /** 접근 가능 페이지 목록 */
    accessPages: string[];
    /** 접근 가능 페이지 (쉼표로 구분된 문자열) */
    accessPagesStr?: string;
    /** 등록일시 */
    insDate?: string;
    /** 등록자 */
    insUser?: string;
    /** 수정일시 */
    updDate?: string;
    /** 수정자 */
    updUser?: string;
}

/** 기반 사용자 유형 */
export type BaseUserType = 'C' | 'S' | 'R';

/** 기반 사용자 유형 라벨 */
export const BASE_USER_TYPE_LABELS: Record<BaseUserType, string> = {
    C: '본사',
    S: '협력사',
    R: '라이더',
};

// ======== API 요청/응답 타입 ========

/** 권한 목록 조회 응답 */
export interface GetAuthorityListResponse {
    authorities: Authority[];
    totalCount: number;
}

/** 권한 생성 요청 */
export interface CreateAuthorityRequest {
    prmCode: string;
    prmName: string;
    prmDesc?: string;
    baseUserType: string;
    accessPages: string[];
    insertUserId: string;
}

/** 권한 수정 요청 */
export interface UpdateAuthorityRequest {
    prmCode: string;
    prmName: string;
    prmDesc?: string;
    baseUserType: string;
    accessPages: string[];
    updateUserId: string;
}

/** 권한 삭제 요청 */
export interface DeleteAuthorityRequest {
    prmCode: string;
    deleteUserId: string;
}

/** 권한 API 응답 */
export interface AuthorityResponse {
    success: boolean;
    message: string;
    prmCode?: string;
}

// ======== 폼 데이터 타입 ========

/** 권한 폼 데이터 */
export interface AuthorityFormData {
    prmCode: string;
    prmName: string;
    prmDesc: string;
    baseUserType: string;
    accessPages: string[];
}

/** 폼 모드 */
export type AuthorityFormMode = 'create' | 'edit' | 'view';

// ======== 모달 Props ========

/** 권한 모달 Props */
export interface AuthorityModalProps {
    isOpen: boolean;
    mode: AuthorityFormMode;
    authority?: Authority | null;
    onClose: () => void;
    onSave: (data: AuthorityFormData) => Promise<void>;
}

// ======== 상수 ========

/**
 * 표준 권한 코드 (수정/삭제 불가)
 * - A: 관리자 (Administrator) - 시스템 전체 관리 권한
 * - C: 본사 (Company/Headquarters) - 본사 직원 권한
 * - S: 협력사 (Supplier) - 협력사 관리자 권한
 * - R: 라이더 (Rider) - 라이더 기본 권한
 */
export const STANDARD_PERMISSION_CODES = ['A', 'C', 'S', 'R'] as const;

/** 표준 권한 코드 라벨 */
export const STANDARD_PERMISSION_LABELS: Record<string, string> = {
    A: '관리자',
    C: '본사',
    S: '협력사',
    R: '라이더',
};

/** 표준 권한 코드 설명 */
export const STANDARD_PERMISSION_DESCRIPTIONS: Record<string, string> = {
    A: '시스템 전체 관리 권한',
    C: '본사 직원 권한',
    S: '협력사 관리자 권한',
    R: '라이더 기본 권한',
};

/** 표준 권한 여부 확인 */
export const isStandardPermission = (prmCode: string): boolean => {
    return STANDARD_PERMISSION_CODES.includes(prmCode as typeof STANDARD_PERMISSION_CODES[number]);
};
